import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { m as motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

type Freq = "monthly" | "campaign" | "test";
type Budget = "small" | "mid" | "large";
type Goal = "always-on" | "big-moment" | "explore";

interface Recommendation {
  tier: "Pulse" | "Prime" | "Pilot";
  href: string;
  tone: "mint" | "butter" | "peach";
  blurb: string;
  why: string[];
}

function recommend(freq: Freq, budget: Budget, goal: Goal): Recommendation {
  if (freq === "test" || (goal === "explore" && budget === "small")) {
    return {
      tier: "Pilot",
      href: "/kontakt",
      tone: "peach",
      blurb: "Testa hela vår process på en enskild produktion innan ni binder upp er.",
      why: [
        "En fullskalig produktion till fast pris (28 995 kr)",
        "Samma team och kvalitet som i Pulse",
        "Naturlig brygga till abonnemang om kemin stämmer",
      ],
    };
  }
  if (freq === "campaign" || goal === "big-moment" || budget === "large") {
    return {
      tier: "Prime",
      href: "/prime",
      tone: "butter",
      blurb: "Skräddarsydda produktioner för era stora ögonblick — brandfilm, kampanj, dokumentär, live.",
      why: [
        "Regissör, manus och full förproduktion",
        "Samlat team — kreativt och tekniskt",
        "Skalbart från enstaka film till hel kampanjsvit",
      ],
    };
  }
  return {
    tier: "Pulse",
    href: "/pulse",
    tone: "mint",
    blurb: "Filmabonnemang som håller takten månad efter månad — utan att ni behöver detaljstyra.",
    why: [
      "Förutsägbar månadsleverans i flera format",
      "Fast pris från 14 995 kr/mån",
      "Strategisk planering ingår — ni styr riktningen",
    ],
  };
}

const opt = <T extends string>(value: T, label: string, sub: string) => ({ value, label, sub });

const freqOptions = [
  opt<Freq>("monthly", "Löpande varje månad", "Stadig närvaro i kanalerna"),
  opt<Freq>("campaign", "Enstaka kampanjer", "Stora moment vid behov"),
  opt<Freq>("test", "Vi vill testa först", "Innan vi binder upp oss"),
];
const budgetOptions = [
  opt<Budget>("small", "Under 30 000 / mån", "eller pilot"),
  opt<Budget>("mid", "30–60 000 / mån", "eller medelstora projekt"),
  opt<Budget>("large", "150 000 kr+", "per enskild produktion"),
];
const goalOptions = [
  opt<Goal>("always-on", "Vara närvarande", "Innehåll som driver konstant"),
  opt<Goal>("big-moment", "Skapa ett stort ögonblick", "Kampanj, lansering, brand"),
  opt<Goal>("explore", "Utforska vad film kan göra", "Vi vet inte än"),
];

export function ServiceMatcher() {
  const [freq, setFreq] = useState<Freq | null>(null);
  const [budget, setBudget] = useState<Budget | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);

  const complete = freq && budget && goal;
  const rec = useMemo(() => (complete ? recommend(freq!, budget!, goal!) : null), [complete, freq, budget, goal]);

  const onComplete = (r: Recommendation) => {
    try {
      sessionStorage.setItem("fenny_recommended_tier", r.tier);
    } catch {
      /* ignore */
    }
    track("service_matcher_complete", { tier: r.tier, freq: freq ?? "", budget: budget ?? "", goal: goal ?? "" });
  };

  return (
    <div className="overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-surface via-surface to-mint/[0.04]">
      <div className="grid gap-0 lg:grid-cols-[1.15fr_1fr]">
        <div className="border-b border-white/10 p-8 lg:border-b-0 lg:border-r lg:p-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-mint">
            <Sparkles className="h-3.5 w-3.5" /> Vilken tjänst passar er?
          </div>
          <h3 className="mt-3 text-2xl font-semibold md:text-3xl">Tre frågor. Konkret svar.</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Vi pekar ut Pulse, Prime eller Pilot baserat på era svar.
          </p>

          <Question
            n="01"
            label="Hur ofta behöver ni film?"
            options={freqOptions}
            value={freq}
            onChange={setFreq}
          />
          <Question
            n="02"
            label="Vilken budget rör vi oss kring?"
            options={budgetOptions}
            value={budget}
            onChange={setBudget}
          />
          <Question
            n="03"
            label="Vad är målet?"
            options={goalOptions}
            value={goal}
            onChange={setGoal}
          />
        </div>

        <div className="relative flex flex-col p-8 lg:p-10">
          <AnimatePresence mode="wait">
            {rec ? (
              <motion.div
                key={rec.tier}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                onAnimationStart={() => onComplete(rec)}
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-mint">
                  <Sparkles className="h-3.5 w-3.5" /> Vi rekommenderar
                </div>
                <div className={cn("mt-3 text-5xl font-semibold leading-none md:text-6xl", {
                  "text-mint": rec.tone === "mint",
                  "text-butter": rec.tone === "butter",
                  "text-peach": rec.tone === "peach",
                })}>
                  {rec.tier}
                </div>
                <p className="mt-5 text-base leading-relaxed text-foreground/90">{rec.blurb}</p>
                <ul className="mt-6 space-y-2.5">
                  {rec.why.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 text-sm text-foreground/85">
                      <span className={cn("mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full", {
                        "bg-mint": rec.tone === "mint",
                        "bg-butter": rec.tone === "butter",
                        "bg-peach": rec.tone === "peach",
                      })} />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to={rec.href}
                    className={cn("inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform hover:-translate-y-0.5", {
                      "bg-mint text-mint-foreground": rec.tone === "mint",
                      "bg-butter text-black": rec.tone === "butter",
                      "bg-peach text-peach-foreground": rec.tone === "peach",
                    })}
                  >
                    Läs mer om {rec.tier} <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm transition-colors hover:border-white/35"
                  >
                    Boka samtal
                  </Link>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex h-full flex-col justify-center"
              >
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Resultat</div>
                <p className="mt-4 text-3xl font-semibold leading-tight text-foreground/40 md:text-4xl">
                  Svara på tre frågor — vi visar rätt nivå.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Question<T extends string>({
  n,
  label,
  options,
  value,
  onChange,
}: {
  n: string;
  label: string;
  options: { value: T; label: string; sub: string }[];
  value: T | null;
  onChange: (v: T) => void;
}) {
  return (
    <div className="mt-7">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">
        {n} · {label}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-xl border p-3 text-left text-sm transition-all",
              value === o.value
                ? "border-mint bg-mint/10 text-foreground"
                : "border-white/10 text-foreground/85 hover:border-white/25",
            )}
          >
            <div className="font-semibold leading-tight">{o.label}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">{o.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
